import "./styles.scss";

import moment from "moment";
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import queryString from "query-string";

import { useClient } from "context/admin/client";

import DashboardContentLayout from "components/layouts/DashboardContentLayout";
import DetailsCard from "components/DetailsCard";
import DetailsCardList from "components/DetailsCardList";
import DevelopmentMobile from "components/DevelopmentMobile";
import ScrollTopMobile from "components/ScrollTopMobile";
import ContactDataModal from "./components/components/ContactDataModal";
import BillingDataModal from "./components/components/BillingDataModal";
import {
  OverviewModal,
  AccessDataModal,
} from "page_components/shared/tasks/modals";

import countries from "libs/countries.json";
import task_status_data from "libs/task_status_data.json";

import priceRender from "libs/helpers/priceRender";
import toHoursAndMinutes from "libs/helpers/toHoursAndMinutes";
import getCountryFromCode from "libs/helpers/getCountryFromCode";
import useResponsive from "libs/helpers/useResponsive";
import plans_data from "libs/plans_data";

import { ReactComponent as ArrowIcon } from "assets/icons/new/forward-arrow.svg";
import { ReactComponent as ThundersIcon } from "assets/icons/new/thunders.svg";
import { ReactComponent as SummaryIcon } from "assets/icons/new/summary.svg";
import { ReactComponent as ErrorIcon } from "assets/icons/new/error.svg";

const actions = [
  {
    type: "send_notification",
  },
  {
    type: "create_task",
  },
];

const mainClass = "clients-admin-details";

const planIcons = {
  safety: plans_data[0].icon,
  basic: plans_data[1].icon,
  premium: plans_data[2].icon,
  "premium / e-commerce": plans_data[2].icon,
};

const ClientDetails = () => {
  const { t } = useTranslation();
  const { max } = useResponsive();
  const { search } = useLocation();
  const navigate = useNavigate();
  const query = queryString.parse(search);
  const clientFilters = {
    status: query.client_status || query.status,
    sort: query.client_sort || query.sort,
    page: query.client_page || query.page,
  };
  const clientsQuery = queryString.stringify(clientFilters);
  const clientReturnQuery = queryString.stringify({
    client_status: clientFilters.status,
    client_sort: clientFilters.sort,
    client_page: clientFilters.page,
  });
  const clientsUrl = `/clients${clientsQuery ? `?${clientsQuery}` : ""}`;

  const { data, loading, error } = useClient();

  const [modal, setModal] = useState(null);

  const toNumber = (value) => {
    const serializedValue =
      value?.$numberDecimal ??
      value?.$numberDouble ??
      value?.$numberInt ??
      value?.$numberLong ??
      value?.value ??
      value;

    if (serializedValue == null || serializedValue === "") return null;

    const numericValue = Number(serializedValue);

    return Number.isFinite(numericValue) ? numericValue : null;
  };

  const formatTime = (value, zeroAsUnavailable = false) => {
    const numericValue = toNumber(value);

    return numericValue === null || (zeroAsUnavailable && numericValue === 0)
      ? "-"
      : toHoursAndMinutes(numericValue)?.formatted;
  };

  let selected_plan = data?.plan?.[0]?.prices?.find(
    (item) => item?.price_id === data?.subscription?.price_id,
  );
  let selected_price = selected_plan?.currencies?.find(
    (item) => item?.currency === data?.subscription?.currency,
  );
  const plan = data?.plan?.[0];
  const planIcon = planIcons[plan?.name?.trim().toLowerCase()];

  const status_notification_label = (status) => {
    switch (status) {
      case 1:
        return t("Everything is fine");
      case 2:
        return t("Something is wrong");
      case 3:
        return t("Awaiting verification");
      case 4:
        return t("Website does not respond");
      default:
        return "-";
    }
  };

  const status_notification_description = (status) => {
    switch (status) {
      case 1:
        return t(
          "Your website is safe and up to date. Your payment method is active.",
        );
      case 2:
        return t(
          "We have detected a problem with your website. We will verify it and fix it, or get back to you with further information",
        );
      case 3:
        return t(
          "It may take up to 24h for us to verify if everything is ok with your website",
        );
      case 4:
        return t(
          "There may be a problem with your website or its server. We have received an automatic notification of the issue and are working to resolve it.",
        );
      default:
        return "-";
    }
  };

  const overview_data = {
    title: t("Overview"),
    iconMobile:
      data?.status === 1 ? (
        <ThundersIcon />
      ) : data?.status === 3 ? (
        <SummaryIcon />
      ) : (
        <ErrorIcon />
      ),
    action: {
      label: t("Edit"),
      iconMobile: <ArrowIcon />,
      onclick: () => setModal("overview"),
    },
    notifications: [
      {
        status: data?.status,
        label: status_notification_label(data?.status),
        description: status_notification_description(data?.status),
      },
    ],
    items: [
      {
        label: t("URL Address"),
        value: data?.website,
      },
      {
        label: t("Last CMS update"),
        value: data?.last_cms_update
          ? moment(data?.last_cms_update).format("ll")
          : "-",
      },
      {
        label: t("SSL certificate valid until"),
        value: data?.ssl_valid_until
          ? moment(data?.ssl_valid_until).format("ll")
          : "-",
      },
      {
        label: t("Domain valid until"),
        value: data?.domain_valid_until
          ? moment(data?.domain_valid_until).format("ll")
          : "-",
      },
      {
        label: t("Time available in your plan"),
        value: toHoursAndMinutes(data?.time_used?.available_in_plan)?.formatted,
        hiddenMobile: true,
      },
      {
        label: t("Time used"),
        value: toHoursAndMinutes(data?.time_used?.time_used)?.formatted,
        hiddenMobile: true,
      },
      {
        label: t("Time left"),
        value: toHoursAndMinutes(data?.time_used?.time_left)?.formatted,
        hiddenMobile: true,
      },
      {
        label: t("Time exceeding plan"),
        value: toHoursAndMinutes(data?.time_used?.time_exceeding_plan)
          ?.formatted,
        hiddenMobile: true,
      },
    ],
  };

  const details_data = [
    {
      title: t("Contact data"),
      action: {
        label: t("Edit"),
        iconMobile: <ArrowIcon />,
        onclick: () => setModal("contact"),
      },
      items: [
        {
          label: t("Full name"),
          value: `${data?.contact_data?.first_name} ${data?.contact_data?.last_name}`,
        },
        {
          label: t("Email"),
          value: data?.contact_data?.email,
        },
        {
          label: t("Phone"),
          value: `${
            countries?.find(
              (item) => item?.code === data?.contact_data?.phone?.code,
            )?.dialling_code || ""
          } ${data?.contact_data?.phone?.number}`,
        },
      ],
    },
    {
      title: t("Tasks"),
      action: {
        label: t("Show"),
        iconMobile: <ArrowIcon />,
        href: `/clients/${data?._id}/tasks${
          clientReturnQuery ? `?${clientReturnQuery}` : ""
        }`,
      },
      items: task_status_data.map((status) => ({
        label: t(status?.label),
        value:
          data?.tasks?.filter((task) => task?.status === status?.value)
            ?.length || "-",
        status: status?.value,
      })),
    },
    {
      title: t("Plan"),
      hiddenMobile: true,
      items: [
        {
          label: t("Type"),
          value: `${data?.plan?.[0]?.name} plan`,
        },
        {
          label: t("Billing cycle"),
          value:
            selected_plan?.contract_period === "monthly"
              ? t("Monthly")
              : t("Yearly"),
        },
        {
          label: t("Amount"),
          value: !!selected_price?.amount
            ? priceRender(
                data?.subscription?.amount / 100,
                data?.subscription?.currency,
              )
            : "-",
        },
        {
          label: t("Next charge"),
          value: !!data?.subscription?.next_payment
            ? moment.unix(data?.subscription?.next_payment)?.format("ll")
            : "-",
        },
      ],
    },
    {
      title: t("Website access data"),
      hiddenDesktop: true,
      action: {
        label: t("View / Edit"),
        iconMobile: <ArrowIcon />,
        onclick: () => setModal("access"),
      },
      items: [
        {
          value: t(
            "Here you can update access data to your website. The data includes access to FTP server, database and CMS WordPress.",
          ),
        },
      ],
    },
  ];

  const payment_data = [
    {
      variantMobile: "plan",
      title: t("Plan"),
      hiddenDesktop: true,
      items: [
        {
          label: t("Type"),
          value: `${data?.plan?.[0]?.name} plan`,
          hiddenMobile: true,
        },
        {
          custom_children: (
            <div className={`${mainClass}__plan-data`}>
              <i>{planIcon}</i>
              <span>{plan?.name}</span>
            </div>
          ),
          hiddenDesktop: true,
        },
        {
          label: t("Billing cycle"),
          value:
            selected_plan?.contract_period === "monthly"
              ? t("Monthly")
              : t("Yearly"),
        },
        {
          label: t("Amount"),
          value: !!selected_price?.amount
            ? priceRender(
                data?.subscription?.amount / 100,
                data?.subscription?.currency,
              )
            : "-",
        },
        {
          label: t("Next charge"),
          value: !!data?.subscription?.next_payment
            ? moment.unix(data?.subscription?.next_payment)?.format("ll")
            : "-",
        },
      ],
    },
    {
      title: t("Payment"),
      view: data?.subscription?.status !== "active" ? "payment_error" : null,
      action: {
        label: t("Verify the payment method"),
        isStatic: true,
      },
      items: [
        {
          type: "card",
          label: t("Method"),
          value: {
            card_type: data?.subscription?.card?.brand,
            status: !!data?.subscription?.card?.expires_soon
              ? 2
              : data?.subscription?.status === "active"
                ? 1
                : 0,
            card_numbers: !!data?.subscription?.card?.last4
              ? `**** **** **** **** ${data?.subscription?.card?.last4}`
              : data?.subscription?.status === "active"
                ? "Link"
                : "-",
          },
        },
        {
          type: "button",
          label: t("Invoices"),
          value: {
            button_label: t("Show"),
            button_href: `/invoices?website=${data?._id}`,
          },
        },
      ],
    },
    {
      title: t("Billing data"),
      action: {
        label: t("Edit"),
        iconMobile: <ArrowIcon />,
        onclick: () => setModal("billing"),
      },
      items: [
        {
          label: t("Name"),
          value: data?.billing_data?.name,
        },
        {
          label: t("Tax identification number"),
          value: `${data?.billing_data?.tax?.code} ${data?.billing_data?.tax?.clean_number}`,
        },
        {
          label: t("Address"),
          value: (
            <>
              {data?.billing_data?.street}
              <br />
              {data?.billing_data?.post_code} {data?.billing_data?.city}
              <br />
              {t(getCountryFromCode(data?.billing_data?.country)?.name)}
            </>
          ),
          align: "top",
        },
      ],
    },
    {
      title: t("Account details"),
      action: {
        label: t("View / Edit"),
        href: `/clients/${data?._id}/account-settings`,
      },
      items: [
        {
          value: t(
            "View or update customer account data and settings in the dashboard.",
          ),
        },
      ],
    },
    {
      title: t("Website access data"),
      hiddenMobile: true,
      action: {
        label: t("View / Edit"),
        onclick: () => setModal("access"),
      },
      items: [
        {
          value: t(
            "Here you can update access data to your website. The data includes access to FTP server, database and CMS WordPress.",
          ),
        },
      ],
    },
  ];

  useEffect(() => {
    if (error) navigate(clientsUrl);
  }, [clientsUrl, error, navigate]);

  if (error) return null;

  return (
    <>
      <DashboardContentLayout
        backMobile={() => navigate(clientsUrl)}
        header={{
          label: data?.website,
          description: max.sm
            ? t("Clients")
            : {
                value: t("Clients"),
                onClick: () => navigate(clientsUrl),
              },
          async: true,
        }}
        loading={loading}
        actions={actions}
      >
        <div className={mainClass}>
          <div className={`${mainClass}__col`}>
            <DetailsCard
              {...overview_data}
              {...(max.sm
                ? {
                    footer: (
                      <DevelopmentMobile
                        available={formatTime(
                          data?.time_used?.available_in_plan,
                          true,
                        )}
                        left={formatTime(data?.time_used?.time_left, true)}
                        exceeding={formatTime(
                          data?.time_used?.time_exceeding_plan,
                        )}
                      />
                    ),
                  }
                : {})}
            >
              <DetailsCardList
                items={overview_data?.items}
                view={overview_data?.view}
              />
            </DetailsCard>
          </div>
          <div className={`${mainClass}__col`}>
            {details_data?.map((data, index) => {
              if (
                (max.sm && data?.hiddenMobile) ||
                (!max.sm && data?.hiddenDesktop)
              )
                return null;

              return (
                <DetailsCard key={index} {...data}>
                  <DetailsCardList
                    {...(data?.title === t("Tasks")
                      ? {
                          variant: "tasks",
                          websiteId: data?._id,
                          clientReturnQuery,
                        }
                      : {})}
                    items={data?.items}
                    view={data?.view}
                  />
                </DetailsCard>
              );
            })}
          </div>
          <div className={`${mainClass}__col ${mainClass}__col--payment`}>
            {payment_data?.map((data, index) => {
              if (
                (max.sm && data?.hiddenMobile) ||
                (!max.sm && data?.hiddenDesktop)
              )
                return null;

              return (
                <DetailsCard key={index} {...data}>
                  <DetailsCardList items={data?.items} view={data?.view} />
                </DetailsCard>
              );
            })}
          </div>
        </div>
        {max.sm && (
          <div className={`${mainClass}__scroll`}>
            <ScrollTopMobile />
          </div>
        )}
      </DashboardContentLayout>

      <ContactDataModal
        isOpen={modal === "contact"}
        onClose={() => setModal(null)}
      />
      <BillingDataModal
        isOpen={modal === "billing"}
        onClose={() => setModal(null)}
      />
      <OverviewModal
        data={data}
        queryDataKey="client"
        isOpen={modal === "overview"}
        onClose={() => setModal(null)}
      />
      <AccessDataModal
        isOpen={modal === "access"}
        onClose={() => setModal(null)}
      />
    </>
  );
};

export default ClientDetails;
